import { useCallback } from 'react';

import { useQuery, UseQueryResult } from '@tanstack/react-query';

import {
	buildLabelsExcludedFromDashboard,
	processPlatformConfiguration
} from 'src/apps/dashboard/helpers/ConfigurationProcessingHelpers';
import { getMfaSessions } from 'src/apps/dashboard/services/mobileDashboardService';
import { TMfaSessionsResult } from 'src/apps/dashboard/types/MobileDashboardServiceTypes';
import { REFETCH_INTERVAL } from 'src/common/consts/QueryConfigs';
import { useConfiguration } from 'src/common/providers/configurationProvider/ConfigurationProvider';
import { DASHBOARD_MFA_SESSIONS } from 'src/common/providers/reactQueryProvider/QueryKeys';
import { queryClient } from 'src/common/providers/reactQueryProvider/ReactQueryProvider';
import { IUseTableQueryParams } from 'src/common/types/UseTableQueryParamsType';

interface IUseMfaSessionsQueryParams extends IUseTableQueryParams {
	dateRange?: string;
}

export const useMfaSessionsQuery = ({
	dateRange,
	filters,
	sort,
	page,
	pageSize
}: IUseMfaSessionsQueryParams): [UseQueryResult<TMfaSessionsResult>, () => void] => {
	const { configuration } = useConfiguration();
	const { coreLabels } = processPlatformConfiguration(configuration?.PLATFORM);
	const labelsExcludedFromDashboard = buildLabelsExcludedFromDashboard(coreLabels);

	const queryResult = useQuery(
		[DASHBOARD_MFA_SESSIONS, filters, sort, page, pageSize, dateRange, labelsExcludedFromDashboard],
		async (): Promise<TMfaSessionsResult> =>
			getMfaSessions({
				dateRange,
				filterOptions: filters,
				sort,
				page,
				pageSize,
				labelsExcludedFromDashboard
			}),
		{
			keepPreviousData: true,
			refetchInterval: REFETCH_INTERVAL,
			staleTime: REFETCH_INTERVAL
		}
	);

	const invalidate = useCallback(
		() =>
			queryClient.invalidateQueries({ queryKey: [DASHBOARD_MFA_SESSIONS], refetchType: 'none' }),
		[]
	);

	return [queryResult, invalidate];
};
